import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 25;

const actionColors = {
  create: "text-[#6EE7B7]", update: "text-[#FCD34D]",
  delete: "text-[#FCA5A5]", login: "text-[#93C5FD]",
  logout: "text-white/60", view: "text-white/60",
};

const RESOURCE_FILTERS = ["", "auth", "users", "services", "portfolio", "blog", "leads", "media", "pages", "site_hero", "site_cta"];

export default function ActivityLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [resource, setResource] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q) params.q = q;
      if (resource) params.resource = resource;
      const { data } = await api.get("/activity", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, resource, page]); // eslint-disable-line

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const renderPager = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-white/60">
      <span>{loading ? "Loading…" : total === 0 ? "No results" : `Showing ${from}–${to} of ${total}`}</span>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={page <= 1 || loading}
          onClick={() => setPage((value) => Math.max(1, value - 1))}
          className="h-8 border border-white/10 text-white/80 hover:text-white disabled:opacity-30"
        >
          Previous
        </Button>
        <span className="text-white/50 min-w-[88px] text-center">Page {page} of {pageCount}</span>
        <Button
          size="sm"
          variant="ghost"
          disabled={page >= pageCount || loading}
          onClick={() => setPage((value) => value + 1)}
          className="h-8 border border-white/10 text-white/80 hover:text-white disabled:opacity-30"
        >
          Next
        </Button>
      </div>
    </div>
  );

  const setFilterQ = (value) => {
    setPage(1);
    setQ(value);
  };
  const setFilterResource = (value) => {
    setPage(1);
    setResource(value);
  };

  return (
    <div>
      <PageHeader
        title="Activity Logs"
        subtitle="Complete audit trail across all modules and users."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={q} onChange={(e) => setFilterQ(e.target.value)} placeholder="Search logs…"
            className="pl-9 bg-black/20 border-white/10 text-white placeholder:text-white/30" />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {RESOURCE_FILTERS.map((r) => (
            <button key={r || "all"} onClick={() => setFilterResource(r)}
              className={`h-9 px-3 rounded-lg text-xs border transition-colors capitalize ${resource === r ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white"}`}>
              {r || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">{renderPager()}</div>

      <GlassCard className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02]">
                <th className="px-5 py-3 font-medium">When</th>
                <th className="px-5 py-3 font-medium">User</th>
                <th className="px-5 py-3 font-medium">Action</th>
                <th className="px-5 py-3 font-medium">Resource</th>
                <th className="px-5 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-white/60 text-xs whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3 text-white">{r.user_name || r.user_email || "System"}</td>
                  <td className={`px-5 py-3 font-medium capitalize ${actionColors[r.action] || "text-white/80"}`}>{r.action}</td>
                  <td className="px-5 py-3 text-white/70 font-mono text-xs">{r.resource}{r.resource_id ? ` / ${String(r.resource_id).slice(-6)}` : ""}</td>
                  <td className="px-5 py-3 text-white/50 font-mono text-xs">{r.ip_address || "—"}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-white/40">No logs match your filters.</td></tr>
              )}
              {loading && items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-white/5">{renderPager()}</div>
      </GlassCard>
    </div>
  );
}
